import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Order } from '../models/order.model';

@Injectable()
export class ProductionPlanService {
  apiURL: string = environment.apiURL;

  automatizationData: EventEmitter<any> = new EventEmitter<any>();
  stepChanged: EventEmitter<any> = new EventEmitter<any>();
  orders: Array<Order> = [];
  operationsForSubmit: Array<any> = [];
  filter = {
    fromDate:  new Date(),
    toDate: new Date(),
    isAutomatizationForPeriod: false,
  };

  constructor(private http: HttpClient) {}

  /**
   * Get production orders
   */
  getProductionOrders(skip: number, limit: number, fromDate: string, toDate: string, locationId: string, sector?: string, machineId?: string, search?: string): Observable<any> {
    let params = new HttpParams().set('skip', skip).set('limit', limit).set('fromDate', fromDate).set('toDate', toDate).set('locationId', locationId);

    if (sector) params = params.append('sector', sector);
    if (machineId) params = params.append('machineId', machineId);
    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/production-tasks/reduced`, { params });
  }

  /**
   * Get operations for single order
   */
  getOperationsForSingleOrder(orderId: string, skip: number, limit: number, fromDate: string, toDate: string, sector?: string, machineId?: string): Observable<any> {
    let params = new HttpParams().set('skip', skip).set('limit', limit).set('fromDate', fromDate).set('toDate', toDate);

    if (sector) params = params.append('sector', sector);
    if (machineId) params = params.append('machineId', machineId);

    return this.http.get(`${this.apiURL}/production-tasks/work-orders/${orderId}`, { params });
  }

  /**
   * Ignore deviation
   */
  ignoreDeviation(orderId: string, operationId?: string): Observable<any> {
    const data = {
      workOrderId: orderId,
      workOrderOperationId: operationId,
    };
    return this.http.patch(`${this.apiURL}/production-tasks/deviation`, data);
  }

  /**
   * Update order operation time
   */
  updateOperationQuantity(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/production-tasks`, data);
  }

  /**
   * Production plan preview
   */
  productionPlanPreview(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/production-tasks/calculate`, data);
  }

  /**
   * Submit production plan
   */
  submitProductionPlanPreview(data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/production-tasks/calculate`, data);
  }

  /**
   * Get production orders
   */
  getProductionSingleOrder(fromDate: string, toDate: string, locationId: string, sector: string, workOrderId: string, machineId?: string): Observable<any> {
    let params = new HttpParams().set('fromDate', fromDate).set('toDate', toDate).set('workOrderId', workOrderId);

    if (locationId) params = params.append('locationId', locationId);
    if (sector) params = params.append('sector', sector);
    if (machineId) params = params.append('machineId', machineId);

    return this.http.get(`${this.apiURL}/production-tasks`, { params });
  }

  /**
   * Emit automatization change
   */
  automatizationChanged(data: Array<Order>): void {
    this.orders = data;
    this.automatizationData.emit(data);
  }

  /**
   * Emit step change
   */
  onStepChanged(step: number): void {
    this.stepChanged.emit(step);
  }

  resetFilter(): void {
    this.filter = {
      fromDate: new Date(),
      toDate: new Date(),
      isAutomatizationForPeriod: false,
    };
  }

  /**
   * Operation time correction
   */
  operationTimeCorrection(
    orderId: string, operationId: string,timeCorrectionEmployee: number, timeCorrectionMachine: number, quantityCorrection: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/operations/${operationId}/time-correction`, {
      timeCorrectionEmployee: timeCorrectionEmployee || 0,
      timeCorrectionMachine: timeCorrectionMachine || 0,
      quantityCorrection: quantityCorrection || 0,
    });
  }

  /**
   * On change operation value
   */
  onChangeOperationValue(tempObject: any): void {
    if (this.operationsForSubmit.length > 0) {
      if (this.operationsForSubmit.some(item => JSON.stringify(item.key) === JSON.stringify(tempObject.key))) {
        const index = this.operationsForSubmit.findIndex(item => JSON.stringify(item.key) === JSON.stringify(tempObject.key));
        if (index !== -1) {
          this.operationsForSubmit[index] = tempObject;
        }
      } else {
        this.operationsForSubmit.push(tempObject);
      }
    } else {
      this.operationsForSubmit.push(tempObject);
    }
  }

  /**
   * On submit operations
   */
  onSubmitOperations(sector: string, addEmployees: boolean, shiftsArray?: any, onChangeDate?: boolean, locationId?: string, machineId?: string): Observable<any> {
    const data: any = {
      sector: sector,
      addEmployees: addEmployees,
      workOrderOperations: shiftsArray || [],
      locationId: locationId || null
    };

    if (!onChangeDate) {
      this.operationsForSubmit.forEach(operation => {
        data.workOrderOperations.push({
          workOrderOperationId: operation.key.workOrderOperationId,
          date: operation.key.date,
          shiftNumber: operation.key.shiftNumber,
          quantityPlanned: operation.quantityPlanned,
          machineId: operation.machineId || machineId || null,
        });
      });
    }

    return this.http.post(`${this.apiURL}/production-tasks/multi`, data);
  }
}
